import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FinalCTA from '@/components/FinalCTA';
import ScrollToTop from '@/components/ScrollToTop';
import SEO from '@/components/SEO';
import {
  FileText,
  Eye,
  Code2,
  Camera,
  Radio,
  Upload,
  FileDown,
  Monitor,
  Users,
  Sparkles,
  ArrowRight,
  ChevronRight,
} from 'lucide-react';

const Features = () => {
  return (
    <div className="flex flex-col min-h-screen bg-dark-bg relative">
      <SEO
        title="Features - SkyTest.ai | AI-Powered Test Automation Platform"
        description="Discover SkyTest.ai's AI-powered test automation features: plain English tests, multi-browser coordination, visual AI navigation, and more."
      />
      {/* Global background effect */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,34,255,0.05)_0%,rgba(10,10,20,0)_70%)] pointer-events-none"></div>

      <Navbar />

      <main className="flex-grow relative">
        {/* Hero Section */}
        <section className="min-h-[55vh] relative flex items-center justify-center section-padding overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div
              className="absolute inset-0 transition-colors duration-500 ease-out"
              style={{
                background: 'linear-gradient(to bottom, hsl(240, 80%, 7%), #0a0a14)',
              }}
            />
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="circuit-pattern-features" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                    <path d="M0,50 H100 M50,0 V100 M25,25 L75,75 M75,25 L25,75" stroke="white" strokeWidth="0.5" fill="none" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#circuit-pattern-features)" />
              </svg>
            </div>
            <div
              className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[60%] h-[40%] rounded-full blur-[100px]"
              style={{
                background: 'radial-gradient(circle at center, hsl(210, 80%, 50%) 0%, rgba(0,34,255,0.05) 70%)',
                opacity: 0.3,
                animation: 'pulse-subtle 8s ease-in-out infinite',
              }}
            />
          </div>

          <div className="container max-w-5xl mx-auto flex flex-col items-center text-center z-10">
            <div className="inline-block animate-fade-down">
              <span className="inline-flex items-center gap-1 py-1 px-3 text-xs font-medium tracking-wider rounded-full bg-white/10 text-white/80 mb-6 neo-blur">
                <Sparkles className="h-3.5 w-3.5 text-blue-300" />
                PLATFORM FEATURES
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight max-w-4xl animate-fade-up text-white">
              Test Smarter, Not Harder
            </h1>

            <p className="text-xl text-gray-300 max-w-3xl mb-10 animate-fade-up">
              From plain English test creation to multi-browser coordination, SkyTest gives you the tools to test real business workflows.
            </p>

            <a
              href="#plain-english"
              className="bg-blue-500 hover:bg-blue-600 py-3 px-8 rounded-full text-white font-medium text-lg transition-all duration-300 focus-ring animate-fade-up inline-flex items-center gap-2"
            >
              Explore Features
              <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </section>

        {/* Demo Video Section */}
        <section className="pt-8 pb-16 md:pb-24 relative bg-gradient-to-b from-[#0a0a14] to-dark-bg">
          <div className="container mx-auto">
            <div className="w-full max-w-4xl mx-auto animate-fade-up">
              <div className="w-full rounded-lg overflow-hidden shadow-lg">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/qYlzKr8LWL8?autoplay=1&mute=1&loop=1&playlist=qYlzKr8LWL8&controls=0&modestbranding=1&rel=0&showinfo=0"
                    title="SkyTest.ai Demo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Plain English Test Creation */}
        <section id="plain-english" className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center animate-fade-up">
                <span className="text-gradient">Plain English Test Creation</span>
              </h2>
              <p className="text-lg text-center text-gray-300 mb-10 animate-fade-up max-w-3xl mx-auto">
                Write tests the way you think about them. No programming knowledge required -- just describe what you want to test in plain English.
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="glass-morphism p-8 rounded-2xl animate-fade-up">
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <FileText className="text-white h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-white">Describe, Don't Code</h3>
                  <p className="text-gray-300 mb-4">
                    SkyTest understands natural language instructions. Write your test cases as simple, numbered steps -- exactly how you would explain the workflow to a colleague.
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      No coding skills needed
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      Business stakeholders can write tests
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      Readable by anyone on the team
                    </li>
                  </ul>
                </div>

                <div className="animate-fade-up" style={{ animationDelay: '0.1s' }}>
                  <div className="rounded-2xl overflow-hidden border border-dark-border">
                    <div className="bg-[#1a1a2e] px-4 py-3 flex items-center gap-2 border-b border-dark-border">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500/60"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500/60"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500/60"></div>
                      </div>
                      <span className="text-gray-400 text-sm ml-2 font-mono">checkout-test.sky</span>
                    </div>
                    <div className="bg-[#0d0d1a] p-6 font-mono text-sm leading-relaxed">
                      <p className="text-blue-300 mb-3">Test: User can complete checkout</p>
                      <p className="text-gray-300"><span className="text-gray-500">1.</span> Navigate to the store homepage</p>
                      <p className="text-gray-300"><span className="text-gray-500">2.</span> Search for "wireless headphones"</p>
                      <p className="text-gray-300"><span className="text-gray-500">3.</span> Click "Add to Cart" on the first result</p>
                      <p className="text-gray-300"><span className="text-gray-500">4.</span> Click the cart icon</p>
                      <p className="text-gray-300"><span className="text-gray-500">5.</span> Click "Proceed to Checkout"</p>
                      <p className="text-green-400"><span className="text-gray-500">6.</span> Verify the order summary shows "wireless headphones"</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Multi-Browser Multi-Role Testing -- Hero Differentiator */}
        <section className="section-padding relative py-24">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-[50%] h-[50%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
            <div className="absolute bottom-1/4 right-1/4 w-[30%] h-[30%] rounded-full bg-purple-500/5 blur-[120px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-flex items-center gap-1 py-1 px-3 text-xs font-medium tracking-wider rounded-full bg-purple-500/20 text-purple-300 mb-4 animate-fade-up">
                  ONLY ON SKYTEST
                </span>
                <h2 className="text-3xl md:text-5xl font-bold mb-4 animate-fade-up">
                  <span className="text-gradient">Multi-Browser, Multi-Role Testing</span>
                </h2>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto animate-fade-up">
                  No other platform does this. Run multiple browser sessions with different user roles at the same time, fully in sync.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>
                {/* Browser A -- Customer */}
                <div className="rounded-2xl overflow-hidden border border-blue-500/30 bg-[#0d0d1a]/80">
                  <div className="bg-blue-500/10 px-4 py-3 flex items-center gap-2 border-b border-blue-500/20">
                    <Monitor className="h-4 w-4 text-blue-400" />
                    <span className="text-blue-300 text-sm font-medium">Browser A -- Customer</span>
                  </div>
                  <div className="p-6 font-mono text-sm leading-relaxed space-y-2">
                    <p className="text-gray-300"><span className="text-blue-400">1.</span> Log in as customer@example.com</p>
                    <p className="text-gray-300"><span className="text-blue-400">2.</span> Browse to "Premium Plan" page</p>
                    <p className="text-gray-300"><span className="text-blue-400">3.</span> Click "Subscribe Now"</p>
                    <p className="text-gray-300"><span className="text-blue-400">4.</span> Enter payment details</p>
                    <p className="text-gray-300"><span className="text-blue-400">5.</span> Click "Confirm Payment"</p>
                    <p className="text-green-400"><span className="text-blue-400">6.</span> Verify "Subscription Active" message</p>
                  </div>
                </div>

                {/* Browser B -- Admin */}
                <div className="rounded-2xl overflow-hidden border border-green-500/30 bg-[#0d0d1a]/80">
                  <div className="bg-green-500/10 px-4 py-3 flex items-center gap-2 border-b border-green-500/20">
                    <Monitor className="h-4 w-4 text-green-400" />
                    <span className="text-green-300 text-sm font-medium">Browser B -- Admin</span>
                  </div>
                  <div className="p-6 font-mono text-sm leading-relaxed space-y-2">
                    <p className="text-gray-400 italic text-xs mb-2">Waits for Browser A step 5 to complete...</p>
                    <p className="text-gray-300"><span className="text-green-400">1.</span> Log in as admin@example.com</p>
                    <p className="text-gray-300"><span className="text-green-400">2.</span> Navigate to "Subscriptions" dashboard</p>
                    <p className="text-gray-300"><span className="text-green-400">3.</span> Search for customer@example.com</p>
                    <p className="text-green-400"><span className="text-green-400">4.</span> Verify subscription status is "Active"</p>
                    <p className="text-green-400"><span className="text-green-400">5.</span> Verify plan shows "Premium Plan"</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 glass-morphism p-6 rounded-2xl animate-fade-up text-center" style={{ animationDelay: '0.2s' }}>
                <p className="text-gray-300">
                  <span className="text-white font-semibold">Coordinated execution:</span> Browser B automatically waits for Browser A to complete the checkout before verifying the admin dashboard. Test real multi-user workflows like marketplace transactions, chat systems, collaborative editing, and approval flows.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* AI Visual Navigation */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -bottom-20 -left-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center animate-fade-up">
                <span className="text-gradient">AI Visual Navigation</span>
              </h2>
              <p className="text-lg text-center text-gray-300 mb-10 animate-fade-up max-w-3xl mx-auto">
                SkyTest looks at your app the way a real user does. No brittle selectors required.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass-morphism p-8 rounded-2xl animate-fade-up">
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Eye className="text-white h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">No CSS Selectors or XPaths</h3>
                  <p className="text-gray-300">
                    Forget fragile locators that break with every UI update. SkyTest's AI identifies elements visually, just like a human tester would.
                  </p>
                </div>

                <div className="glass-morphism p-8 rounded-2xl animate-fade-up" style={{ animationDelay: '0.1s' }}>
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Users className="text-white h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">Interacts Like a Human</h3>
                  <p className="text-gray-300">
                    The AI sees the screen, understands context, and interacts with your app naturally -- clicking buttons, filling forms, and navigating menus.
                  </p>
                </div>

                <div className="glass-morphism p-8 rounded-2xl animate-fade-up" style={{ animationDelay: '0.2s' }}>
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Sparkles className="text-white h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">Adapts to UI Changes</h3>
                  <p className="text-gray-300">
                    Redesigned your button? Moved a menu item? The AI adapts automatically. No more maintaining hundreds of brittle selectors.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hybrid Steps */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -right-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center animate-fade-up">
                <span className="text-gradient">Hybrid Steps</span>
              </h2>
              <p className="text-lg text-center text-gray-300 mb-10 animate-fade-up max-w-3xl mx-auto">
                Mix plain English instructions with Playwright code blocks for when you need pixel-perfect precision.
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="animate-fade-up">
                  <div className="rounded-2xl overflow-hidden border border-dark-border">
                    <div className="bg-[#1a1a2e] px-4 py-3 flex items-center gap-2 border-b border-dark-border">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500/60"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500/60"></div>
                        <div className="w-3 h-3 rounded-full bg-green-500/60"></div>
                      </div>
                      <span className="text-gray-400 text-sm ml-2 font-mono">hybrid-test.sky</span>
                    </div>
                    <div className="bg-[#0d0d1a] p-6 font-mono text-sm leading-relaxed space-y-2">
                      <p className="text-blue-300 mb-1">Test: Validate API response on checkout</p>
                      <p className="text-gray-300"><span className="text-gray-500">1.</span> Navigate to the product page</p>
                      <p className="text-gray-300"><span className="text-gray-500">2.</span> Click "Add to Cart"</p>
                      <p className="text-gray-300"><span className="text-gray-500">3.</span> Click "Proceed to Checkout"</p>
                      <p className="text-purple-300 mt-2 mb-1">```playwright</p>
                      <p className="text-yellow-200 pl-4">const response = await page.waitForResponse(</p>
                      <p className="text-yellow-200 pl-6">r =&gt; r.url().includes('/api/order')</p>
                      <p className="text-yellow-200 pl-4">);</p>
                      <p className="text-yellow-200 pl-4">expect(response.status()).toBe(200);</p>
                      <p className="text-purple-300">```</p>
                      <p className="text-green-400"><span className="text-gray-500">4.</span> Verify "Order confirmed" message appears</p>
                    </div>
                  </div>
                </div>

                <div className="glass-morphism p-8 rounded-2xl animate-fade-up" style={{ animationDelay: '0.1s' }}>
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Code2 className="text-white h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-white">Best of Both Worlds</h3>
                  <p className="text-gray-300 mb-4">
                    Use natural language for UI interactions and drop into Playwright code when you need to validate APIs, check network requests, or perform complex assertions.
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      Full Playwright API access within tests
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      Validate API responses and network calls
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      Custom JavaScript assertions
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Screenshot Evidence */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -bottom-20 -right-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center animate-fade-up">
                <span className="text-gradient">Screenshot Evidence</span>
              </h2>
              <p className="text-lg text-center text-gray-300 mb-10 animate-fade-up max-w-3xl mx-auto">
                Every action gets a timestamped screenshot, so you can see exactly what happened during each test run.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass-morphism p-8 rounded-2xl animate-fade-up">
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Camera className="text-white h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">Timestamped Captures</h3>
                  <p className="text-gray-300">
                    Every step produces a screenshot with a precise timestamp. Know exactly what the UI looked like at every point in the test.
                  </p>
                </div>

                <div className="glass-morphism p-8 rounded-2xl animate-fade-up" style={{ animationDelay: '0.1s' }}>
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Eye className="text-white h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">Visual Debugging</h3>
                  <p className="text-gray-300">
                    When a test fails, see exactly what went wrong. No more guessing -- the screenshot shows the state of the application at the moment of failure.
                  </p>
                </div>

                <div className="glass-morphism p-8 rounded-2xl animate-fade-up" style={{ animationDelay: '0.2s' }}>
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <FileText className="text-white h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">Stakeholder Reporting</h3>
                  <p className="text-gray-300">
                    Share screenshots with teammates who don't read code. Everyone on the team can understand what happened.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Real-Time Monitoring */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -left-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center animate-fade-up">
                <span className="text-gradient">Real-Time Monitoring</span>
              </h2>
              <p className="text-lg text-center text-gray-300 mb-10 animate-fade-up max-w-3xl mx-auto">
                Watch your tests run live. No more waiting for a run to finish to see what happened.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass-morphism p-8 rounded-2xl animate-fade-up">
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Radio className="text-white h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">Live Execution Stream</h3>
                  <p className="text-gray-300">
                    Watch tests run step by step in real time. See each action as it happens, with live status updates streamed to your browser.
                  </p>
                </div>

                <div className="glass-morphism p-8 rounded-2xl animate-fade-up" style={{ animationDelay: '0.1s' }}>
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Camera className="text-white h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">Live Screenshot Updates</h3>
                  <p className="text-gray-300">
                    Screenshots stream in as they are captured. See the browser view update in near-real-time as the AI navigates through your application.
                  </p>
                </div>

                <div className="glass-morphism p-8 rounded-2xl animate-fade-up" style={{ animationDelay: '0.2s' }}>
                  <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                    <Sparkles className="text-white h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">Instant Failure Notifications</h3>
                  <p className="text-gray-300">
                    Know the moment something goes wrong. Failures surface immediately in the live stream so you can investigate without delay.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* File Upload + Import/Export -- Combined row */}
        <section className="section-padding relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -bottom-20 -right-20 w-[40%] h-[40%] rounded-full bg-brand/5 blur-[150px] animate-pulse-subtle" />
          </div>

          <div className="container mx-auto">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* File Upload Support */}
                <div className="animate-fade-up">
                  <h2 className="text-3xl font-bold mb-4">
                    <span className="text-gradient">File Upload Support</span>
                  </h2>
                  <div className="glass-morphism p-8 rounded-2xl">
                    <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                      <Upload className="text-white h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-white">Test with Real Documents</h3>
                    <p className="text-gray-300 mb-4">
                      Upload PDFs, images, spreadsheets, and other files as part of your test workflow. Test document-heavy processes end to end.
                    </p>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        PDFs, images, spreadsheets
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        Document-heavy workflow testing
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        File validation and verification
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Import/Export */}
                <div className="animate-fade-up" style={{ animationDelay: '0.1s' }}>
                  <h2 className="text-3xl font-bold mb-4">
                    <span className="text-gradient">Import / Export</span>
                  </h2>
                  <div className="glass-morphism p-8 rounded-2xl">
                    <div className="h-10 w-10 rounded-full bg-brand/20 flex items-center justify-center mb-6">
                      <FileDown className="text-white h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-white">Markdown Portability</h3>
                    <p className="text-gray-300 mb-4">
                      Write tests in markdown, import existing test documentation, and export for version control. Your tests live alongside your code.
                    </p>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        Write and import from markdown
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        Export for documentation
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        Version control friendly
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <FinalCTA />
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Features;
